import { useFormContext, useFieldArray, FieldErrors } from 'react-hook-form';
import { Group, Button, Stack, Text, Card, Flex } from '@mantine/core';
import { Plus, GitBranch, Flag, InfoIcon, X } from 'lucide-react';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';

import SelectWithIcon from '../Select/SelectWithIcon';

import { FormInput } from '../Input';
import { useActiveProductAssetsQuery } from '../../../../hooks/queries/useActiveProductAssetsQuery';
import {
  type StepQuestionType,
  type StepVideoType,
} from '@pages/sessions/new/types';
import { SessionConfig } from '@pages/sessions/new/utils/experienceForm/transform';

type StepFieldProps = {
  name: string; // "steps"
};

type StepErrors = FieldErrors<SessionConfig['steps'][number]> & {
  message: string;
};

// Helper per accedere agli errori in modo type-safe
function getStepErrors(
  errors: FieldErrors<SessionConfig> | undefined,
  idx: number
): StepErrors | undefined {
  if (!errors?.steps || !Array.isArray(errors.steps)) {
    return undefined;
  }
  return errors.steps[idx] as StepErrors | undefined;
}

export function ConfigurationStepsField({ name }: StepFieldProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });
  const activeAssets = useActiveProductAssetsQuery();

  const generateStepId = useCallback(() => {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).substring(2, 10);
  }, []);

  const handleAddStep = useCallback(() => {
    append({
      id: generateStepId(),
      type: 'VIDEO',
      videoId: '',
      audioId: '',
      order: fields.length + 1,
    } as StepVideoType);
  }, [append, generateStepId, fields.length]);

  const handleAddQuestion = useCallback(() => {
    append({
      id: generateStepId(),
      type: 'QUESTION',
      order: fields.length + 1,
      text: '',
      answers: [],
    } as StepQuestionType);
  }, [append, generateStepId, fields.length]);

  // Empty state when no steps
  if (fields.length === 0) {
    return (
      <Stack gap='md' align='center' py='xl'>
        <Text fw={500} size='lg' c='dimmed'>
          Lista degli Step dell'esperienza
        </Text>
        <Text size='sm' c='dimmed' ta='center' maw={400}>
          Inizia aggiungendo uno step video o una domanda per creare la tua
          esperienza
        </Text>
        <Group gap='md' mt='md'>
          <Button
            size='md'
            variant='outline'
            onClick={handleAddStep}
            leftSection={<Plus size={16} />}
          >
            Aggiungi Step
          </Button>
          <Button
            size='md'
            variant='outline'
            onClick={handleAddQuestion}
            leftSection={<Plus size={16} />}
          >
            Aggiungi Domanda
          </Button>
        </Group>
        {errors.steps?.message && (
          <Text size='sm' c='red' mt='sm'>
            {errors.steps.message as string}
          </Text>
        )}
      </Stack>
    );
  }

  // When steps exist, show them with buttons at the bottom
  return (
    <Stack gap='md'>
      <Text fw={500}>Lista degli Step dell'esperienza</Text>
      {fields.map((field, idx) => (
        <Card key={field.id} withBorder p='md'>
          <Stack key={field.id} align='flex-start' gap='md'>
            <Group justify='space-between' w='100%'>
              <Text>Step {idx + 1}</Text>
              <Button
                variant='light'
                color='red'
                size='xs'
                onClick={() => remove(idx)}
              >
                Rimuovi
              </Button>
            </Group>

            {/* Campi specifici per tipo step */}
            <ConditionalStepFields
              idx={idx}
              name={name}
              typeFieldPath={`${name}.${idx}.type`}
              assets={activeAssets}
            />
          </Stack>
        </Card>
      ))}

      {/* Footer with add buttons */}
      <Group justify='flex-end' gap='xs' pt='sm'>
        <Button
          size='xs'
          variant='outline'
          onClick={handleAddStep}
          leftSection={<Plus size={14} />}
        >
          Aggiungi Step
        </Button>
        <Button
          size='xs'
          variant='outline'
          onClick={handleAddQuestion}
          leftSection={<Plus size={14} />}
        >
          Aggiungi Domanda
        </Button>
      </Group>

      {errors.steps?.message && (
        <Text size='sm' c='red'>
          {errors.steps.message as string}
        </Text>
      )}
    </Stack>
  );
}

// Helper per mostrare i campi specifici rispetto al tipo di step
function ConditionalStepFields({
  idx,
  name,
  typeFieldPath,
  assets,
}: {
  idx: number;
  name: string;
  typeFieldPath: string;
  assets: ReturnType<typeof useActiveProductAssetsQuery>;
}) {
  const { watch } = useFormContext();
  const type = watch(typeFieldPath);

  if (type === 'VIDEO') {
    return <VideoStepField name={name} idx={idx} assets={assets} />;
  }
  if (type === 'QUESTION') {
    return (
      <>
        <FormInput
          name={`${name}.${idx}.text`}
          label='Testo domanda'
          placeholder='Testo della domanda'
          w='100%'
        />
        <AnswersField name={name} idx={idx} />
      </>
    );
  }
}

function AnswersField({ name, idx }: { name: string; idx: number }) {
  const { control, watch, getValues, setValue } = useFormContext();

  const steps: Array<StepVideoType | StepQuestionType> = watch('steps');
  // react-hook-form richiede un path come "steps.0.answers"
  const fieldArrayName = `${name}.${idx}.answers`;
  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldArrayName,
  });

  const availableStepIds = useMemo(() => {
    const nextSteps = steps
      .filter((_, stepIdx) => stepIdx > idx)
      .map(step => String((step as any).id ?? ''))
      .filter(id => id);

    return new Set<string>([...nextSteps, '-1']);
  }, [steps, idx]);

  const nextSteps = useMemo(() => {
    return steps.reduce<
      Array<{ value: string; label: string; icon: ReactNode }>
    >((acc, step, stepIdx) => {
      if (stepIdx <= idx) {
        return acc;
      }

      const value = String((step as any).id ?? '');
      if (!value) {
        return acc;
      }

      const stepNumber = step.order ?? stepIdx + 1;
      acc.push({
        value,
        label: `Step ${stepNumber}`,
        icon: <GitBranch size={16} />,
      });

      return acc;
    }, []);
  }, [steps, idx]);

  useEffect(() => {
    fields.forEach((_, answerIdx) => {
      const valuePath = `${name}.${idx}.answers.${answerIdx}.goToStepId`;
      const currentValue = getValues(valuePath) as string | undefined;

      if (currentValue && !availableStepIds.has(currentValue)) {
        setValue(valuePath, '', {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    });
  }, [availableStepIds, fields, getValues, idx, name, setValue]);

  return (
    <Stack gap='xs' w='100%'>
      {fields.map((field, answerIdx) => {
        const selectData = [];

        if (nextSteps.length > 0) {
          selectData.push({
            group: 'Step successivi',
            items: nextSteps,
          });
        }

        // Aggiungi sempre l'opzione "Fine Esperienza"
        selectData.push({
          group: 'Fine Esperienza',
          items: [
            {
              value: '-1',
              label: 'Fine Esperienza',
              icon: <Flag size={16} />,
            },
          ],
        });

        return (
          <Flex
            w='100%'
            key={field.id}
            align='flex-start'
            gap='sm'
            style={{ minWidth: 0 }}
          >
            <FormInput
              name={`${name}.${idx}.answers.${answerIdx}.label`}
              label={answerIdx === 0 ? 'Risposta' : ''}
              placeholder='Risposta'
              w={'100%'}
            />
            <SelectWithIcon
              name={`${name}.${idx}.answers.${answerIdx}.goToStepId`}
              required
              label={answerIdx === 0 ? 'Seleziona uno step' : undefined}
              placeholder={'Seleziona uno step'}
              data={selectData}
              w={'100%'}
            />
            <Button
              style={{ minWidth: 'fit-content' }}
              type='button'
              color='red'
              size='sm'
              variant='subtle'
              onClick={() => remove(answerIdx)}
              disabled={fields.length <= 1}
              mt={answerIdx === 0 ? 25 : 0}
            >
              Rimuovi
            </Button>
          </Flex>
        );
      })}

      {nextSteps.length === 0 && (
        <Group gap={4} align='center' mb={2}>
          <InfoIcon
            size={16}
            style={{ color: 'var(--mantine-color-blue-6)' }}
          />
          <Text size='xs' c='dimmed'>
            Prima di poter associare una risposta a uno step successivo, crea
            almeno uno step dopo questo. In alternativa, puoi selezionare "Fine
            Esperienza" per concludere il percorso.
          </Text>
        </Group>
      )}

      <Button
        type='button'
        color='blue'
        size='sm'
        variant='outline'
        onClick={() => {
          const newAnswer: StepQuestionType['answers'][number] = {
            id: Math.random().toString(36).substring(2),
            label: '',
            goToStepId: '', // Inizializza il campo goToStepId per assicurarsi che sia presente
          };
          append(newAnswer);
        }}
      >
        + Aggiungi risposta
      </Button>
    </Stack>
  );
}

type VideoStepFieldProps = {
  name: string;
  idx: number;
  assets: ReturnType<typeof useActiveProductAssetsQuery>;
};

function VideoStepField({ name, idx, assets }: VideoStepFieldProps) {
  const {
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useFormContext();
  const stepErrors = getStepErrors(errors, idx);
  const videoFieldPath = `${name}.${idx}.videoId`;
  const audioFieldPath = `${name}.${idx}.audioId`;
  const selectedVideoId = watch(videoFieldPath) as string | null;
  const previousVideoIdRef = useRef<string | null>(null);
  const isInitialMountRef = useRef(true);

  const videoData = useMemo(() => {
    const groups = assets.videoGroups.map(group => ({
      group: group.group,
      items: group.items,
    }));

    // Add "Nessun video" option at the beginning
    return [
      {
        group: 'Opzioni',
        items: [
          {
            value: '',
            label: 'Nessun video (solo audio)',
            icon: <X size={16} />,
          },
        ],
      },
      ...groups,
    ];
  }, [assets.videoGroups]);

  // Get all audios grouped by product/video for standalone audio selection
  const allAudiosGrouped = useMemo(() => {
    const groups: Array<{
      group: string;
      items: Array<{
        value: string;
        label: string;
        subtitle?: string;
        icon?: string;
      }>;
    }> = [];

    assets.rawProductAssets.forEach(asset => {
      asset.videos.forEach(video => {
        const audioOptions = assets.audioOptionsByVideoId[video.id] ?? [];
        if (audioOptions.length > 0) {
          groups.push({
            group: `${asset.product.name} · ${video.name}`,
            items: audioOptions.map(option => ({
              value: option.value,
              label: option.label,
              subtitle: option.subtitle,
              icon: typeof option.icon === 'string' ? option.icon : undefined,
            })),
          });
        }
      });
    });

    return groups;
  }, [assets.rawProductAssets, assets.audioOptionsByVideoId]);

  const audioOptions =
    selectedVideoId != null && selectedVideoId !== ''
      ? (assets.audioOptionsByVideoId[Number(selectedVideoId)] ?? [])
      : [];

  const audioData = useMemo(() => {
    // If "Nessun video" is selected (empty string) or no video selected, show all audios grouped (standalone)
    if (!selectedVideoId || selectedVideoId === '') {
      return allAudiosGrouped;
    }
    // If a specific video is selected, show only audios for that video
    if (!audioOptions.length) {
      return [];
    }
    return [
      {
        group: 'Audio disponibili',
        items: audioOptions,
      },
    ];
  }, [audioOptions, selectedVideoId, allAudiosGrouped]);

  useEffect(() => {
    // Only clear audio if a specific video is selected and audio was tied to that video
    if (selectedVideoId && selectedVideoId !== '') {
      const currentAudio = getValues(audioFieldPath) as string | undefined;
      const audioValues = new Set(audioOptions.map(option => option.value));

      if (currentAudio && !audioValues.has(currentAudio)) {
        setValue(audioFieldPath, '', {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    }
  }, [audioFieldPath, audioOptions, getValues, selectedVideoId, setValue]);

  useEffect(() => {
    const currentVideoId = selectedVideoId ?? null;
    const previousVideoId = previousVideoIdRef.current;

    // Skip on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      previousVideoIdRef.current = currentVideoId;
      return;
    }

    // Always reset audio when video changes (from any value to any other value)
    // This includes going from null to a value, from a value to null, or from one value to another
    if (previousVideoId !== currentVideoId) {
      setValue(audioFieldPath, '', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }

    previousVideoIdRef.current = currentVideoId;
  }, [
    audioFieldPath,
    selectedVideoId,
    setValue,
    assets.audioOptionsByVideoId,
    getValues,
  ]);

  const isLoadingVideos =
    assets.isLoading && videoData.length === 0 && !assets.error;
  const isVideoSelectDisabled = isLoadingVideos || videoData.length === 0;
  // Audio can be selected only if "Nessun video" is selected (for standalone) or if a video is selected (for video-specific audio)
  const isAudioSelectDisabled = isLoadingVideos
    ? true
    : selectedVideoId && selectedVideoId !== ''
      ? audioOptions.length === 0
      : allAudiosGrouped.length === 0;

  const videoPlaceholder = assets.error
    ? 'Errore nel caricamento dei video'
    : isLoadingVideos
      ? 'Caricamento video...'
      : videoData.length === 0
        ? 'Nessun video disponibile'
        : 'Scegli un video (opzionale)';

  const audioPlaceholder = assets.error
    ? 'Errore nel caricamento degli audio'
    : isLoadingVideos
      ? 'Caricamento audio...'
      : selectedVideoId && selectedVideoId !== ''
        ? audioOptions.length === 0
          ? 'Nessun audio disponibile per questo video'
          : 'Scegli un audio'
        : allAudiosGrouped.length === 0
          ? 'Nessun audio disponibile'
          : 'Scegli un audio (seleziona "Nessun video (solo audio)" per audio standalone)';

  const handleVideoChange = useCallback(
    (value: string | null) => {
      const newVideoId = value ?? null;
      const currentVideoId = selectedVideoId ?? null;

      // Always reset audio when video changes
      if (newVideoId !== currentVideoId) {
        setValue(audioFieldPath, '', {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    },
    [selectedVideoId, audioFieldPath, setValue, getValues]
  );

  return (
    <Stack gap='md' w='100%'>
      <SelectWithIcon
        name={videoFieldPath}
        label='Seleziona Video (opzionale)'
        placeholder={videoPlaceholder}
        data={videoData}
        disabled={isVideoSelectDisabled}
        onChange={handleVideoChange}
      />
      <SelectWithIcon
        key={`audio-${selectedVideoId ?? 'none'}`}
        name={audioFieldPath}
        label='Seleziona Audio'
        placeholder={audioPlaceholder}
        data={audioData}
        disabled={isAudioSelectDisabled}
      />
      {stepErrors && (
        <Text size='xs' c='red'>
          {stepErrors.message}
        </Text>
      )}
      {assets.error && (
        <Text size='xs' c='red'>
          {assets.error.message}
        </Text>
      )}
    </Stack>
  );
}
